import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mic, Square, Loader2, Radio, CheckCircle2, AlertCircle, Clock, Calendar, CloudOff, Video, Headphones, MonitorPlay } from 'lucide-react';
import { enqueueChunk, countChunks } from '@/lib/chunkQueue';
import { drainQueue } from '@/lib/chunkUploader';
import { captureAudio, canCaptureTab, meetingPlatformLabel } from '@/lib/audioCapture';

const CHUNK_INTERVAL_MS = 20000; // Send audio every 20 seconds

// Generate a unique id without importing anything heavy
const chunkId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function SessionRecording({ user, token, onLogout }) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [micPermission, setMicPermission] = useState(null);
  const [checkingPermission, setCheckingPermission] = useState(true);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [sessionTiming, setSessionTiming] = useState({ startTime: null, endTime: null, sessionDate: null });
  const [chunkStatuses, setChunkStatuses] = useState([]);
  const [finalizing, setFinalizing] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  // Telehealth: chosen capture method before recording starts.
  // We now only offer tab-audio capture (mic-only was removed from the
  // UI at user request). mic_only is still used internally as a safety
  // fallback if the user cancels the tab picker mid-flow.
  const [captureMethod, setCaptureMethod] = useState(null);
  const [starting, setStarting] = useState(false); // guard while we're requesting permissions

  // For telehealth sessions, auto-select mic_plus_tab as soon as we
  // know the mode — there's only one option now, so don't force the
  // provider to click twice before they can start.
  useEffect(() => {
    if (session?.session_mode === 'telehealth' && !captureMethod) {
      setCaptureMethod('mic_plus_tab');
    }
  }, [session?.session_mode, captureMethod]);

  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const chunkIntervalRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const isRecordingRef = useRef(false);
  const pendingRequestsRef = useRef(0);
  const transcriptContainerRef = useRef(null);
  // Callback returned by captureAudio() that stops mic + tab tracks
  // and closes the AudioContext. Called on stopRecording / unmount.
  const audioCleanupRef = useRef(null);

  useEffect(() => {
    fetchSession();
    checkMicrophonePermission();
    return () => {
      clearAllIntervals();
      stopMediaStream();
    };
  }, [sessionId]);

  // --- Offline chunk queue wiring ---------------------------------
  // Drain + keep the badge accurate whenever:
  //   • this session screen mounts (recover queued chunks from prior visits)
  //   • the browser flips online
  //   • the service worker posts a DRAIN_CHUNKS message (from Background Sync)
  const refreshQueueCount = useCallback(async () => {
    try {
      const n = await countChunks(sessionId);
      setQueuedCount(n);
    } catch {}
  }, [sessionId]);

  const tryDrain = useCallback(async (reason = 'mount') => {
    const result = await drainQueue(API, {
      onChange: ({ sessionId: sid, status }) => {
        if (sid === sessionId && status === 'done') {
          toast.success(`Offline chunk uploaded (${reason})`, { duration: 1500 });
        }
      },
    });
    if (result.drained > 0) {
      // live transcript won't auto-update because we went around axios,
      // but the backend has the chunk now and the final transcript on
      // stop-recording will include it.
    }
    await refreshQueueCount();
  }, [sessionId, refreshQueueCount]);

  useEffect(() => {
    refreshQueueCount();
    // Drain any chunks left from a previous page-load
    tryDrain('page load');

    const onOnline = () => {
      setOnline(true);
      toast.success('Back online — uploading queued audio…', { duration: 2000 });
      tryDrain('online');
    };
    const onOffline = () => {
      setOnline(false);
      toast.warning('You\'re offline — audio is being cached locally and will auto-upload when you reconnect.', { duration: 3500 });
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    const onSWMessage = (event) => {
      if (event.data?.type === 'DRAIN_CHUNKS') tryDrain(event.data.source || 'sw');
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSWMessage);
    }

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSWMessage);
      }
    };
  }, [tryDrain, refreshQueueCount]);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [liveTranscript]);

  const clearAllIntervals = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (chunkIntervalRef.current) clearInterval(chunkIntervalRef.current);
  };

  const stopMediaStream = () => {
    // Run the audioCapture cleanup first — it stops mic + tab tracks
    // and closes the AudioContext. Fall back to direct track-stop in
    // case a legacy path set mediaStreamRef without a cleanup fn.
    try { audioCleanupRef.current?.(); } catch {}
    audioCleanupRef.current = null;
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const checkMicrophonePermission = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicPermission('unsupported');
        setCheckingPermission(false);
        toast.error('Your browser does not support audio recording');
        return;
      }
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const status = await navigator.permissions.query({ name: 'microphone' });
          setMicPermission(status.state);
          status.onchange = () => setMicPermission(status.state);
        } catch {
          setMicPermission('prompt');
        }
      } else {
        setMicPermission('prompt');
      }
    } catch {
      setMicPermission('prompt');
    } finally {
      setCheckingPermission(false);
    }
  };

  const fetchSession = async () => {
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(res.data);
    } catch {
      toast.error('Failed to load session');
      navigate('/');
    }
  };

  const createMediaRecorder = (stream) => {
    let options = { audioBitsPerSecond: 64000 };
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
      options.mimeType = 'audio/webm;codecs=opus';
    } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
      options.mimeType = 'audio/ogg;codecs=opus';
    } else {
      options.mimeType = 'audio/webm';
    }
    const recorder = new MediaRecorder(stream, options);
    audioChunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };
    recorder.start(5000); // collect data every 5s
    return recorder;
  };

  const sendChunkForTranscription = useCallback(async () => {
    if (!isRecordingRef.current) return;
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;

    // Stop current recorder to get a complete blob
    const currentRecorder = mediaRecorderRef.current;
    const mimeType = currentRecorder.mimeType || 'audio/webm';

    return new Promise((resolve) => {
      currentRecorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const currentIndex = chunkIndexRef.current;
        chunkIndexRef.current += 1;

        // Immediately restart recording with the same stream
        if (isRecordingRef.current && mediaStreamRef.current) {
          mediaRecorderRef.current = createMediaRecorder(mediaStreamRef.current);
        }

        // Skip tiny blobs (no real audio)
        if (blob.size < 1000) {
          resolve();
          return;
        }

        // Determine file extension
        let ext = 'webm';
        if (mimeType.includes('ogg')) ext = 'ogg';

        // Send chunk in background
        const statusEntry = { index: currentIndex, status: 'sending' };
        setChunkStatuses(prev => [...prev, statusEntry]);
        pendingRequestsRef.current += 1;

        try {
          const formData = new FormData();
          formData.append('audio', blob, `chunk_${currentIndex}.${ext}`);

          const res = await axios.post(
            `${API}/sessions/${sessionId}/transcribe-chunk?chunk_index=${currentIndex}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              },
              timeout: 60000
            }
          );

          if (res.data.chunk_text) {
            setLiveTranscript(prev => {
              const separator = prev ? ' ' : '';
              return prev + separator + res.data.chunk_text;
            });
          }

          setChunkStatuses(prev =>
            prev.map(s => s.index === currentIndex
              ? { ...s, status: res.data.status === 'ok' ? 'done' : res.data.status }
              : s
            )
          );
        } catch (err) {
          console.error(`Chunk ${currentIndex} failed:`, err);
          // Offline or transient network error — stash the chunk in IndexedDB
          // so it can upload when connectivity returns (elevator / basement recovery).
          const isNetworky = !navigator.onLine || !err.response;
          if (isNetworky) {
            try {
              await enqueueChunk({
                id: chunkId(),
                sessionId,
                chunkIndex: currentIndex,
                blob,
                ext,
                token,
              });
              await refreshQueueCount();
              // Register a Background Sync so Chrome/Edge/Android will retry
              // once connectivity returns even if the tab is closed.
              if ('serviceWorker' in navigator && 'SyncManager' in window) {
                try {
                  const reg = await navigator.serviceWorker.ready;
                  await reg.sync.register('upload-chunks');
                } catch {}
              }
              setChunkStatuses(prev =>
                prev.map(s => s.index === currentIndex ? { ...s, status: 'queued' } : s)
              );
            } catch (qerr) {
              console.error('Failed to queue offline chunk:', qerr);
              setChunkStatuses(prev =>
                prev.map(s => s.index === currentIndex ? { ...s, status: 'error' } : s)
              );
            }
          } else {
            setChunkStatuses(prev =>
              prev.map(s => s.index === currentIndex ? { ...s, status: 'error' } : s)
            );
          }
        } finally {
          pendingRequestsRef.current -= 1;
          resolve();
        }
      };

      currentRecorder.stop();
    });
  }, [sessionId, token]);

  const startRecording = async (forcedMethod = null) => {
    if (starting || recording) return;
    const sessionMode = session?.session_mode === 'telehealth' ? 'telehealth' : 'in_person';
    // For telehealth we need the user to have picked a method first
    // (mic_plus_tab OR mic_only). For in-person we go straight in.
    const chosenMethod = forcedMethod || captureMethod || (sessionMode === 'in_person' ? 'mic_only' : null);
    if (sessionMode === 'telehealth' && !chosenMethod) {
      // Shouldn't happen — the picker UI blocks this — but be defensive.
      toast.error('Pick a capture method first');
      return;
    }
    setStarting(true);

    try {
      if (micPermission !== 'granted') {
        toast.info('Requesting microphone access...', { duration: 2000 });
      }

      // ── NEW: unified capture pipeline ──
      // in_person            → mic-only, echo cancel ON
      // telehealth/mic_only  → mic-only, echo cancel OFF, 48 kHz
      // telehealth/mic+tab   → mic + shared-tab audio mixed via Web Audio
      let captureResult;
      try {
        captureResult = await captureAudio(sessionMode, chosenMethod);
      } catch (error) {
        console.error('captureAudio error:', error);
        setMicPermission('denied');
        setStarting(false);
        if (error.name === 'NotAllowedError') {
          toast.error('Permission denied. Allow microphone' + (chosenMethod === 'mic_plus_tab' ? ' + tab-sharing' : '') + ' access in your browser.', { duration: 6000 });
        } else if (error.name === 'NotFoundError') {
          toast.error('No microphone found.', { duration: 5000 });
        } else {
          toast.error('Failed to start capture: ' + error.message, { duration: 5000 });
        }
        return;
      }

      const { stream, cleanup, capture_method: actualMethod, meeting_platform, warnings } = captureResult;
      warnings?.forEach((w) => toast.warning(w, { duration: 5000 }));

      setMicPermission('granted');
      mediaStreamRef.current = stream;
      audioCleanupRef.current = cleanup;

      // Reset state
      chunkIndexRef.current = 0;
      setLiveTranscript('');
      setChunkStatuses([]);
      isRecordingRef.current = true;

      // Create and start the recorder
      mediaRecorderRef.current = createMediaRecorder(stream);
      setRecording(true);
      setRecordingTime(0);

      // Mark start time in the provider's tz AND tell backend which
      // capture method was used so Whisper + diarization prompts can
      // tune themselves for telehealth.
      try {
        const qs = new URLSearchParams();
        if (actualMethod) qs.set('capture_method', actualMethod);
        if (meeting_platform && meeting_platform !== 'unknown') qs.set('meeting_platform', meeting_platform);
        const qsStr = qs.toString();
        const res = await axios.post(
          `${API}/sessions/${sessionId}/start-recording${qsStr ? `?${qsStr}` : ''}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSessionTiming(prev => ({
          ...prev,
          startTime: res.data.start_time,
          sessionDate: res.data.session_date
        }));
      } catch {}

      // Timer
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // Chunk sending interval
      chunkIntervalRef.current = setInterval(() => {
        sendChunkForTranscription();
      }, CHUNK_INTERVAL_MS);

      const modeLabel = sessionMode === 'telehealth'
        ? (actualMethod === 'mic_plus_tab' ? 'Telehealth (mic + tab audio)' : 'Telehealth (mic only)')
        : 'In-person';
      toast.success(`Recording — ${modeLabel}`, { duration: 2500 });
      if (meeting_platform && meeting_platform !== 'unknown') {
        toast.info(`Detected ${meetingPlatformLabel(meeting_platform)} — transcription tuned for this platform`, { duration: 3500 });
      }
    } finally {
      setStarting(false);
    }
  };

  const stopRecording = async () => {
    if (!isRecordingRef.current) return;

    isRecordingRef.current = false;
    clearAllIntervals();
    setRecording(false);
    setFinalizing(true);

    // Send final chunk
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
        await new Promise((resolve) => {
          mediaRecorderRef.current.onstop = async () => {
            const blob = new Blob(audioChunksRef.current, { type: mimeType });
            const currentIndex = chunkIndexRef.current;

            if (blob.size >= 1000) {
              let ext = 'webm';
              if (mimeType.includes('ogg')) ext = 'ogg';

              setChunkStatuses(prev => [...prev, { index: currentIndex, status: 'sending' }]);

              try {
                const formData = new FormData();
                formData.append('audio', blob, `chunk_${currentIndex}.${ext}`);
                const res = await axios.post(
                  `${API}/sessions/${sessionId}/transcribe-chunk?chunk_index=${currentIndex}`,
                  formData,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'multipart/form-data'
                    },
                    timeout: 60000
                  }
                );
                if (res.data.chunk_text) {
                  setLiveTranscript(prev => prev ? prev + ' ' + res.data.chunk_text : res.data.chunk_text);
                }
                setChunkStatuses(prev =>
                  prev.map(s => s.index === currentIndex ? { ...s, status: 'done' } : s)
                );
              } catch (err) {
                console.error('Final chunk failed:', err);
                // Stash the final chunk too — auto-uploads on reconnect.
                const isNetworky = !navigator.onLine || !err.response;
                if (isNetworky) {
                  try {
                    await enqueueChunk({
                      id: chunkId(),
                      sessionId,
                      chunkIndex: currentIndex,
                      blob,
                      ext,
                      token,
                    });
                    await refreshQueueCount();
                    if ('serviceWorker' in navigator && 'SyncManager' in window) {
                      try {
                        const reg = await navigator.serviceWorker.ready;
                        await reg.sync.register('upload-chunks');
                      } catch {}
                    }
                    setChunkStatuses(prev =>
                      prev.map(s => s.index === currentIndex ? { ...s, status: 'queued' } : s)
                    );
                  } catch {
                    setChunkStatuses(prev =>
                      prev.map(s => s.index === currentIndex ? { ...s, status: 'error' } : s)
                    );
                  }
                } else {
                  setChunkStatuses(prev =>
                    prev.map(s => s.index === currentIndex ? { ...s, status: 'error' } : s)
                  );
                }
              }
            }
            resolve();
          };
          mediaRecorderRef.current.stop();
        });
      }
    } catch (err) {
      console.error('Error sending final chunk:', err);
    }

    // Wait for any pending requests
    let waitCount = 0;
    while (pendingRequestsRef.current > 0 && waitCount < 30) {
      await new Promise(r => setTimeout(r, 1000));
      waitCount++;
    }

    stopMediaStream();

    // Mark end time in EST
    try {
      const res = await axios.post(
        `${API}/sessions/${sessionId}/stop-recording?duration_seconds=${recordingTime}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSessionTiming(prev => ({ ...prev, endTime: res.data.end_time }));
    } catch {}

    // Finalize the transcript
    try {
      await axios.post(
        `${API}/sessions/${sessionId}/finalize-transcript`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transcript finalized! Redirecting...');
      setTimeout(() => navigate(`/session/${sessionId}/transcript`), 1500);
    } catch (err) {
      console.error('Finalize error:', err);
      toast.error(err.response?.data?.detail || 'Failed to finalize transcript');
    } finally {
      setFinalizing(false);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const doneChunks = chunkStatuses.filter(s => s.status === 'done').length;
  const sendingChunks = chunkStatuses.filter(s => s.status === 'sending').length;
  const errorChunks = chunkStatuses.filter(s => s.status === 'error').length;
  const queuedChunks = chunkStatuses.filter(s => s.status === 'queued').length;

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #f8f4ff 50%, #e8eaf6 100%)' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-white/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            data-testid="back-button"
            className="rounded-xl border-gray-200 hover:bg-gray-50 shrink-0 px-3 sm:px-4"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 capitalize truncate">
            {session.session_mode === 'telehealth' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-semibold" data-testid="session-mode-badge-telehealth">
                <Video className="w-3 h-3" /> Telehealth
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-semibold" data-testid="session-mode-badge-in-person">
                <Headphones className="w-3 h-3" /> In-person
              </span>
            )}
            <span className="hidden sm:inline">{session.session_type} Session</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">

          {/* Left: Recording Controls */}
          <div className="lg:col-span-2">
            <Card className="p-5 sm:p-8 bg-white/80 backdrop-blur-xl rounded-3xl border border-white/50 shadow-2xl">
              <div className="text-center">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1" data-testid="page-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Session Recording
                </h1>
                <p className="text-sm text-gray-500 mb-6">Real-time transcription</p>

                {/* Telehealth capture info — shown only for telehealth
                    sessions that haven't started yet. Patient audio is
                    captured directly from the shared meeting tab; we
                    prompt the user to pick it when they hit start. */}
                {session.session_mode === 'telehealth' && !recording && !finalizing && (
                  <div className="mb-6 text-left" data-testid="telehealth-capture-picker">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">How patient audio is captured</p>
                    <div className="flex items-start gap-3 p-3 rounded-xl border-2 border-purple-500 bg-purple-50" data-testid="capture-method-mic-plus-tab">
                      <MonitorPlay className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">Share meeting tab</div>
                        <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">Works with Zoom, Google Meet, Doxy, SimplePractice, Teams — captures patient audio directly from the tab. When you click start, your browser will ask which tab to share — pick the meeting tab and tick <span className="font-semibold">&quot;Share tab audio&quot;</span>.</div>
                        {!canCaptureTab() && (
                          <div className="text-[11px] text-amber-700 mt-1 font-medium">This browser doesn&apos;t support tab sharing. Use Chrome or Edge for accurate patient transcription.</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Record Button */}
                <div className="mb-6">
                  <div className="relative inline-flex items-center justify-center mb-6">
                    {recording && (
                      <div className="absolute inset-0 rounded-full bg-red-500 opacity-20 blur-2xl animate-pulse" />
                    )}
                    <button
                      onClick={recording ? stopRecording : () => startRecording()}
                      disabled={
                        finalizing || checkingPermission || starting ||
                        (!recording && session.session_mode === 'telehealth' && !captureMethod)
                      }
                      data-testid={recording ? 'stop-recording-button' : 'start-recording-button'}
                      className={`relative w-32 h-32 sm:w-28 sm:h-28 rounded-full flex items-center justify-center transition-all shadow-2xl active:scale-95 ${
                        finalizing || starting ? 'bg-gray-400 cursor-not-allowed' :
                        (!recording && session.session_mode === 'telehealth' && !captureMethod) ? 'bg-gray-300 cursor-not-allowed' :
                        recording
                          ? 'bg-red-500 hover:bg-red-600'
                          : 'bg-gradient-to-br from-purple-600 to-slate-600 hover:from-purple-700 hover:to-slate-700'
                      }`}
                    >
                      {finalizing || starting ? (
                        <Loader2 className="w-12 h-12 sm:w-10 sm:h-10 text-white animate-spin" />
                      ) : recording ? (
                        <Square className="w-12 h-12 sm:w-10 sm:h-10 text-white" />
                      ) : (
                        <Mic className="w-12 h-12 sm:w-10 sm:h-10 text-white" />
                      )}
                    </button>
                  </div>

                  <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 tabular-nums" data-testid="recording-timer">
                    {formatTime(recordingTime)}
                  </div>

                  <p className="text-sm text-gray-600" data-testid="recording-status">
                    {finalizing ? 'Finalizing transcript...' :
                     starting ? 'Requesting permissions…' :
                     recording ? 'Recording — transcribing in real-time...' :
                     session.session_mode === 'telehealth' ? 'Click to start — you\'ll be asked to pick the meeting tab' :
                     'Click to start recording'}
                  </p>

                  {/* Offline + queued-chunks indicator */}
                  {(!online || queuedCount > 0) && (
                    <div
                      className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                        !online ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-blue-50 text-blue-800 border border-blue-200'
                      }`}
                      data-testid="offline-queue-indicator"
                    >
                      <CloudOff className="w-3.5 h-3.5" />
                      {!online ? (
                        <span>Offline — {queuedCount > 0 ? `${queuedCount} chunk${queuedCount > 1 ? 's' : ''} queued, ` : ''}recording continues locally</span>
                      ) : (
                        <span>{queuedCount} chunk{queuedCount > 1 ? 's' : ''} queued · uploading…</span>
                      )}
                    </div>
                  )}

                  {/* Session Timing (provider's timezone) */}
                  {(sessionTiming.startTime || session?.start_time) && (
                    <div className="mt-4 bg-slate-50 rounded-xl p-3 text-left space-y-1.5" data-testid="session-timing">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Calendar className="w-3.5 h-3.5 text-purple-500" />
                        <span className="font-medium">Date:</span>
                        <span>{sessionTiming.sessionDate || session?.session_date || '—'}</span>
                        <span className="text-gray-400">({(() => {
                          try {
                            const tz = session?.timezone || user?.timezone;
                            if (!tz) return 'EST';
                            const s = new Date().toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' });
                            const label = s.split(' ').pop();
                            return label && !label.startsWith('AM') && !label.startsWith('PM') ? label : tz;
                          } catch { return 'EST'; }
                        })()})</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Clock className="w-3.5 h-3.5 text-green-500" />
                        <span className="font-medium">Start:</span>
                        <span>{sessionTiming.startTime || session?.start_time || '—'}</span>
                      </div>
                      {(sessionTiming.endTime || session?.end_time) && (
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Clock className="w-3.5 h-3.5 text-red-500" />
                          <span className="font-medium">End:</span>
                          <span>{sessionTiming.endTime || session?.end_time}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Chunk Status Bar */}
                {chunkStatuses.length > 0 && (
                  <div className="bg-gray-50 rounded-2xl p-4 text-left" data-testid="chunk-status-panel">
                    <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Transcription Progress</p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {chunkStatuses.map((s) => (
                        <div
                          key={s.index}
                          data-testid={`chunk-${s.index}-status`}
                          className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium ${
                            s.status === 'done' ? 'bg-green-100 text-green-700' :
                            s.status === 'sending' ? 'bg-yellow-100 text-yellow-700 animate-pulse' :
                            s.status === 'queued' ? 'bg-amber-100 text-amber-700' :
                            s.status === 'error' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {s.status === 'done' ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                           s.status === 'sending' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                           s.status === 'queued' ? <CloudOff className="w-3.5 h-3.5" /> :
                           s.status === 'error' ? <AlertCircle className="w-3.5 h-3.5" /> :
                           s.index + 1}
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                      {doneChunks > 0 && <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-600" />{doneChunks} done</span>}
                      {sendingChunks > 0 && <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 text-yellow-600 animate-spin" />{sendingChunks} processing</span>}
                      {queuedChunks > 0 && <span className="flex items-center gap-1"><CloudOff className="w-3 h-3 text-amber-600" />{queuedChunks} queued for upload</span>}
                      {errorChunks > 0 && <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3 text-red-600" />{errorChunks} failed</span>}
                    </div>
                  </div>
                )}

                {/* Info */}
                <div className="mt-6 bg-purple-50 border border-purple-200 rounded-xl p-3">
                  <p className="text-xs text-purple-800">
                    Audio is transcribed every 20 seconds during recording. The full transcript is ready the moment you stop.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Right: Live Transcript */}
          <div className="lg:col-span-3">
            <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border border-white/50 shadow-2xl overflow-hidden h-full flex flex-col">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                {recording && <Radio className="w-4 h-4 text-red-500 animate-pulse" />}
                <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Live Transcript
                </h2>
                {liveTranscript && (
                  <span className="ml-auto text-xs text-gray-400">{liveTranscript.split(/\s+/).length} words</span>
                )}
              </div>
              <div
                ref={transcriptContainerRef}
                data-testid="live-transcript-content"
                className="flex-1 p-6 overflow-y-auto min-h-[400px] max-h-[600px]"
              >
                {liveTranscript ? (
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {liveTranscript}
                    {recording && <span className="inline-block w-1.5 h-4 bg-purple-500 ml-0.5 animate-pulse rounded-sm" />}
                  </p>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <Mic className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">
                        {recording ? 'Listening... transcript will appear here shortly' : 'Start recording to see live transcription'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
